import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, User, Building2, AlertTriangle, CheckCircle2 } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import PasswordField from "@/components/auth/PasswordField";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { getInvite, acceptInvite } from "@/api/auth";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { validateFullName } from "@/lib/validation";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null); // { valid, email, name, company_name }
  const [form, setForm] = useState({ name: "", password: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setChecking(false); setInvite({ valid: false }); return undefined; }
    getInvite(token)
      .then((res) => { if (!cancelled) { setInvite(res); setForm((f) => ({ ...f, name: res.name || "" })); } })
      .catch(() => { if (!cancelled) setInvite({ valid: false }); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    const next = {};
    const nameError = validateFullName(form.name);
    if (nameError) next.name = nameError;
    if (form.password.length < 8) next.password = "Use at least 8 characters.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      const res = await acceptInvite({ token, name: form.name.trim(), password: form.password });
      const session = await loginWithToken(res.access_token);
      navigate(homeForRole(session?.user?.role || "tester"), { replace: true });
    } catch (err) {
      setErrors({ password: err.message || "Could not accept the invitation." });
      setSubmitting(false);
    }
  }

  // ---- loading -------------------------------------------------------------
  if (checking) {
    return (
      <AuthLayout title="Checking your invitation…" subtitle="One moment.">
        <div className="flex items-center justify-center py-10 text-slate-400">
          <Loader2 size={26} className="animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  // ---- invalid / expired ---------------------------------------------------
  if (!invite || !invite.valid) {
    const accepted = invite && invite.reason === "already_accepted";
    return (
      <AuthLayout
        title={accepted ? "Invitation already used" : "Invitation not valid"}
        subtitle={accepted ? "This invite has already been accepted." : "This link is invalid or has expired."}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p>{accepted
              ? "You can sign in with the password you set earlier."
              : "Ask your company admin to send you a fresh invitation."}</p>
          </div>
          {/* Button renders its own Link when given `to` — a <button> inside an
              <a> is invalid HTML. */}
          <Button to="/login" className="w-full">
            Go to sign in <ArrowRight size={18} />
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // ---- accept form ---------------------------------------------------------
  return (
    <AuthLayout
      title="Accept your invitation"
      subtitle="Set your name and password to join your team."
    >
      <div className="mb-5 flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50/70 p-4 dark:border-brand-500/20 dark:bg-brand-500/10">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/15 text-brand-600 dark:text-brand-300">
          <Building2 size={20} />
        </span>
        <div className="min-w-0 text-sm">
          <p className="font-semibold text-slate-900 dark:text-white">
            {invite.company_name || "Your team"}
          </p>
          <p className="truncate text-slate-500 dark:text-slate-400">Joining as {invite.email}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Input
          id="name"
          label="Full name"
          icon={User}
          placeholder="Your name"
          value={form.name}
          onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setErrors((er) => ({ ...er, name: undefined })); }}
          error={errors.name}
        />
        <PasswordField
          value={form.password}
          onChange={(e) => { setForm((f) => ({ ...f, password: e.target.value })); setErrors((er) => ({ ...er, password: undefined })); }}
          error={errors.password}
          showStrength
          placeholder="Create a password"
        />
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? (
            <><Loader2 size={18} className="animate-spin" /> Setting up your account…</>
          ) : (
            <><CheckCircle2 size={18} /> Accept & create account</>
          )}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        Already have an account?{" "}
        <Link to="/login" className="font-semibold text-brand-600 hover:text-brand-500 dark:text-brand-400">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
